import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  deleteKpiBranchAssignment,
  getKpiAssignmentMetaData,
  getKpiBranchAssignments,
  postKpiBranchAssignments,
} from '@/api/kpi'
import { pushNotification, useAppDispatch } from '@/store'
import {
  KpiAssignmentMetaDataModel,
  KpiAssignmentMetaDataResponseModel,
  KpiBranchAssignmentBodyModel,
  KpiBranchAssignmentCreationModel,
  KpiBranchAssignmentsModel,
} from '@/types'

interface FetchBranchAssignmentsParams {
  branchName?: string
  year?: number
  quarter?: number
  kpiMetric?: string
}

interface UseKpiBranchGoalAssignmentsReturn {
  metadata: KpiAssignmentMetaDataModel | null
  branchAssignments: KpiBranchAssignmentsModel[] | null
  loading: boolean
  fetchMetadata: (branchName: string) => Promise<void>
  fetchBranchAssignments: (
    params?: FetchBranchAssignmentsParams
  ) => Promise<void>
  createBranchAssignments: (
    assignments: KpiBranchAssignmentCreationModel[]
  ) => Promise<void>
  deleteBranchAssignment: (id: string) => Promise<void>
}

const KPI_CATEGORY_MAPPING: Record<string, string> = {
  sales: 'Sales',
  labor: 'Labor',
  speed_of_service: 'Speed Of Service',
  loyalty: 'Loyalty',
  spend: 'Spend',
} as const

const useKpiBranchGoalAssignments = (
  initialBranchName?: string
): UseKpiBranchGoalAssignmentsReturn => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [metadata, setMetadata] = useState<KpiAssignmentMetaDataModel | null>(
    null
  )
  const [branchAssignments, setBranchAssignments] = useState<
    KpiBranchAssignmentsModel[] | null
  >(null)
  const [loading, setLoading] = useState<boolean>(false)

  const fetchMetadata = useCallback(async (branchName: string) => {
    if (!branchName) {
      setMetadata(null)
      return
    }

    setLoading(true)

    try {
      const data: KpiAssignmentMetaDataResponseModel =
        await getKpiAssignmentMetaData(branchName)

      const transformedMetadata: KpiAssignmentMetaDataModel = {
        branch: data.branch,
        eligibleUsers: data.eligible_users?.map((user) => ({
          id: user.id,
          username: user.username,
          email: user.email,
          fullName: user.full_name,
        })),
        availableKpis: Object.fromEntries(
          Object.entries(data.available_kpis)?.map(([key, kpis]) => [
            KPI_CATEGORY_MAPPING[key] || key,
            kpis.map((kpi) => ({
              metricField: kpi.metric_field,
              name: kpi.name,
              unit: kpi.unit,
            })),
          ])
        ),
      }

      setMetadata(transformedMetadata)
    } catch (err) {
      const error =
        err instanceof Error
          ? err
          : new Error('Failed to fetch KPI assignment metadata')

      dispatch(
        pushNotification({
          type: 'error',
          title: t('common.errors.serverError'),
          description: error.message,
          duration: 5000,
        })
      )
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchBranchAssignments = useCallback(
    async (params?: FetchBranchAssignmentsParams) => {
      setLoading(true)

      try {
        const data = await getKpiBranchAssignments(
          params?.branchName,
          params?.year,
          params?.quarter,
          params?.kpiMetric
        )

        const assignments: KpiBranchAssignmentsModel[] = data.map(
          (assignment) => ({
            id: assignment.id,
            kpiMetric: assignment.kpi_metric,
            branchId: assignment.branch_id,
            branchName: assignment.branch_name,
            year: assignment.year,
            quarter: assignment.quarter,
            targetValue: assignment.target_value,
            bonusAmount: assignment.bonus_amount,
            createdAt: assignment.created_at,
            kpiName: assignment.kpi_name,
            kpiUnit: assignment.kpi_unit,
            kpiGroup:
              KPI_CATEGORY_MAPPING[assignment.kpi_group] ||
              assignment.kpi_group,
            granularity: assignment.granularity,
            lowerIsBetter: assignment.lower_is_better,
          })
        )

        setBranchAssignments(assignments)
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error('Failed to fetch KPI branch assignments')

        dispatch(
          pushNotification({
            type: 'error',
            title: t('common.errors.serverError'),
            description: error.message,
            duration: 5000,
          })
        )
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const createBranchAssignments = useCallback(
    async (newAssignments: KpiBranchAssignmentCreationModel[]) => {
      setLoading(true)

      const assignments: KpiBranchAssignmentBodyModel[] = newAssignments.map(
        (assignment) => ({
          branch_name: assignment.branchName,
          kpi_metric: assignment.kpiMetric,
          year: assignment.year,
          quarter: assignment.quarter,
          target_value: assignment.targetValue,
        })
      )

      try {
        await postKpiBranchAssignments(assignments)
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error('Failed to create KPI branch assignments')

        dispatch(
          pushNotification({
            type: 'error',
            title: t('common.errors.serverError'),
            description: error.message,
            duration: 5000,
          })
        )
        throw error
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const deleteBranchAssignment = useCallback(async (id: string) => {
    setLoading(true)

    try {
      await deleteKpiBranchAssignment(id)
      setBranchAssignments((prev) =>
        prev ? prev.filter((assignment) => assignment.id !== id) : prev
      )
    } catch (err) {
      const error =
        err instanceof Error
          ? err
          : new Error('Failed to delete KPI branch assignment')

      dispatch(
        pushNotification({
          type: 'error',
          title: t('common.errors.serverError'),
          description: error.message,
          duration: 5000,
        })
      )
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (initialBranchName) {
      fetchMetadata(initialBranchName)
    }
  }, [])

  return {
    metadata,
    branchAssignments,
    loading,
    fetchMetadata,
    fetchBranchAssignments,
    createBranchAssignments,
    deleteBranchAssignment,
  }
}

export default useKpiBranchGoalAssignments
